import { isEmptyArray } from 'src/app/common/utils/object.extensions';
import { debounceTime, delay, share } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  fromEvent,
  Observable,
  scan,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { VisitorEvent } from '../../models/visitor.event';
import { VisitorPlace } from '../../models/visitor.place';

@Component({
  selector: 'ig-infinite-scroll-container',
  templateUrl: './infinite.scroll.container.component.html',
  styleUrls: ['./infinite.scroll.container.component.scss'],
})
export class InfiniteScrollContainerComponent extends BaseComponent implements AfterViewInit {
  reloadAnimation = false;

  offset = new BehaviorSubject(null);
  data$: Observable<VisitorEvent[] | VisitorPlace[]>;

  isAllDataLoaded = false;
  isLoadingItems = true;
  isShowSkeleton = true;

  @Input() loadMore: IInfiniteScrollLoadMoreEvent = null;
  @Input() mode: 'normal' | 'virtual' = 'virtual';
  @Input() isEvent: boolean = true;
  @Input() searchFields: string[] = [];
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
  @ViewChild('container', { static: false }) public container: ElementRef;

  constructor(
    public activatedRoute: ActivatedRoute,
    public mediaQueryService: MediaQueryService,
    public cdRef: ChangeDetectorRef
  ) {
    super({ activatedRoute });

    this.organizeData();

    this.mediaQueryService.isMobile$.subscribe(() => {});
    this.mediaQueryService.resized.subscribe(() => {
      const promotion = this.frontendSettings?.customSettings.visitorPromotion;
      this.setGridForFancyCard(0, promotion.isDisabled && !!promotion.image);
    });
  }

  get cardStyle() {
    return this.isEvent
      ? this.frontendSettings?.customSettings.calendarStyle.event
      : this.frontendSettings?.customSettings.calendarStyle.place;
  }

  organizeData() {
    this.data$ = this.offset.pipe(
      // throttleTime(500),
      takeUntil(this.isDestroyed),
      tap(() => {
        this.isLoadingItems = true;
        this.isShowSkeleton = this.loadMore.isClearBeforeLoad;
        this.reloadAnimation = this.isShowSkeleton;
      }),
      switchMap(() => this.loadMore.loadMoreFn()),
      delay(200),
      tap((data) => {
        if (data.length === 0) {
          this.isAllDataLoaded = true;
        }
        this.isLoadingItems = false;
        this.reloadAnimation = false;
      }),
      scan((acc, batch) => {
        let hasPromotion = false;

        if (this.loadMore.isClearBeforeLoad) {
          acc = [];

          const promotion = this.frontendSettings?.customSettings.visitorPromotion;
          if (!this.loadMore.isSearch && !promotion.isDisabled && promotion.image) {
            acc = [
              {
                cover: {
                  source: promotion.image,
                },
                socialName: 'promotion',
                hostLink: promotion.link,
                id: 'promotion-item', // Add a fixed ID for the promotion item
              },
            ];
            hasPromotion = true;
            this.setHeightForPromotion();
          }
          this.isAllDataLoaded = false;
          this.loadMore.isClearBeforeLoad = false;
        }

        // Create a map of existing items by ID for efficient lookup
        const existingItemsMap = new Map();
        acc.forEach((item) => {
          // Use a safe approach to get a unique identifier
          const itemId = this.getItemId(item);
          if (itemId) {
            existingItemsMap.set(itemId, true);
          }
        });
        // Filter out duplicates from the new batch
        const uniqueNewItems = batch.filter((item) => {
          // Get a reliable identifier for this item
          const itemId = this.getItemId(item);
          // If the item has no reliable ID, we can't deduplicate it effectively
          if (!itemId) return true;
          // Check if this item already exists in our accumulated list
          const isDuplicate = existingItemsMap.has(itemId);
          return !isDuplicate;
        });

        this.setGridForFancyCard(acc.length, hasPromotion);
        return [...acc, ...uniqueNewItems];
      }, []),
      tap(() => {
        timer(2000)
          .pipe(
            tap(() => {
              // for some clients use Wix. One upgrade of Wix causes the list not to be updated. Refer to https://itidigital.atlassian.net/browse/DXP-2040
              this.cdRef.detectChanges();
            })
          )
          .subscribe();
      }),
      share()
    );
  }

  ngAfterViewInit(): void {
    fromEvent(this.container.nativeElement, 'scroll')
      .pipe(
        debounceTime(100),
        tap((e) => {
          if (this.isLoadingItems || this.isAllDataLoaded) return;

          const { scrollTop, scrollHeight, clientHeight } = this.container.nativeElement;

          if (scrollHeight - scrollTop < clientHeight + 300) {
            this.isLoadingItems = true;
            this.isShowSkeleton = false;
            this.offset.next(EMPTY);
          }
        })
      )
      .subscribe();
  }

  public scrolledIndexChange(index: number) {
    if (this.isLoadingItems || this.isAllDataLoaded) return;

    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();

    if (end === total) {
      this.isLoadingItems = true;
      this.offset.next(EMPTY);
    }
  }

  public trackByIndex(index: number, item: any) {
    return item?.id || index;
    return item.id || `index-${index}`;
  }
  // Helper method to get a reliable unique identifier for items
  public getItemId(item: any): string | undefined {
    if (!item) return undefined;
    if (item.id !== undefined) return String(item.id);
    if (item.name && item.startTime) {
      return `${item.name}-${item.startTime}`;
    }
    if (item.name && item.cover?.source) {
      return `${item.name}-${item.cover.source.substring(0, 50)}`;
    }
    return undefined;
  }

  // promtion image is inserted into the first item of the array
  // if there are just a few items, the promotion image height is not correct
  // so we need to set the height of the promotion image equal to the first event item
  private setHeightForPromotion() {
    setTimeout(() => {
      const promotionEl = this.container.nativeElement.querySelector('.promotion') as HTMLElement;
      if (!promotionEl) {
        return;
      }

      const eventStyle = this.frontendSettings?.customSettings.calendarStyle.event;
      const cardSelector = `ig-visitor-${eventStyle}-card .bg-white`;
      const visitorCards = Array.from(
        this.container.nativeElement.querySelectorAll(cardSelector)
      ) as HTMLElement[];

      if (visitorCards.length > 2) {
        return;
      }
    });
  }

  /**
   * For FancyCards, this function picks one of the Facebook events (which images are more clearer) each row spans 2 columns for the first batch (16) events (so load more on scroll will not apply)
   * @param from if from = 0 ==> loaded the first batch (16) events; otherwise it is load more on scroll down
   * @returns
   */
  private setGridForFancyCard(from: number = 0, hasPromotion: boolean = false) {
    // ignore scroll down load more
    if (hasPromotion && from > 1) return;
    if (!hasPromotion && from > 0) return;

    setTimeout(() => {
      let fancyCards = this.container.nativeElement.querySelectorAll(
        'ig-visitor-fancy-card'
      ) as HTMLElement[];

      if (isEmptyArray(fancyCards)) return;

      const cards: HTMLElement[] = Array.from(fancyCards).slice(from);
      if (!cards || cards.length === 0) return;

      // to reload events from event filters, existing cards will keep the span-2 css class, remove them first
      cards.forEach((x) => {
        x.classList.remove('span-2');
      });

      const grid = this.container.nativeElement;

      if (!grid) return;

      const gridRect = grid.getBoundingClientRect();
      if (gridRect.width <= 1080) return; // turn off for mobile

      const rect = cards[0].getBoundingClientRect();

      // calculate the column and row count
      const columns = Math.floor(gridRect.width / rect.width);
      const rows = Math.floor(cards.length / columns);

      // this count is the facebook event card that have been set spans 2 column,
      // when one card is set to span 2 columns, the rest cards need to move 1 position
      let count = 0;
      for (let i = 0; i < rows; i++) {
        const start = i * columns - count;
        const end = i * columns + columns - 1 - count;

        // promotion image spans 2 columns
        const currentRow = cards.slice(start, hasPromotion ? end - 2 : end);

        // get a random facebook event in the row to make it spans 2 columns
        const random = currentRow
          .filter(
            (x) =>
              x.attributes['socialname'] &&
              x.attributes['socialname']['nodeValue'].includes('facebook')
          )
          .getRandom();
        if (random) {
          (random as HTMLElement).classList.add('span-2');
          count++;
        }
      }
    });
  }
}

export interface IInfiniteScrollLoadMoreEvent {
  loadMoreFn: <T>() => Observable<Array<T>>;
  isClearBeforeLoad: boolean;
  isSearch?: boolean;
}
